/**
 * URL d'un sprite rendu — **versionnée**, et c'est tout l'intérêt du fichier.
 *
 * ── Le défaut que ça corrige, et pourquoi il est grave ────────────────────────
 *
 * Les sprites vivent dans `public/`, donc Vite ne les renomme pas : leur URL est stable
 * d'une version à l'autre. Or le service worker les met en cache **`CacheFirst`, 30
 * jours** (P3.11 de l'audit) et le navigateur les met en cache tout seul. Un joueur qui
 * revient après un re-rendu reçoit donc, dans le pire des cas, l'**ancien PNG servi au
 * nouveau moteur**.
 *
 * Tant que l'ancre valait (0,5 · 0,5), l'incohérence était bénigne : un sprite d'une
 * autre taille restait centré. Depuis que l'ancre d'un bâtiment se **dérive de la hauteur
 * de sa texture**, elle ne l'est plus du tout — l'ancre calculée pour une image de 608 px
 * appliquée à une image de 762 px décale le bâtiment de 150 px vers le haut. **Les
 * bâtiments volent**, sur un moteur juste et des sprites justes, simplement parce qu'ils
 * ne viennent pas de la même version.
 *
 * ── Pourquoi la version dans l'URL, et pas un autre correctif ─────────────────
 *
 * `StaleWhileRevalidate` ne suffit pas : il sert l'ancienne image **une fois** avant de
 * la remplacer, donc le défaut apparaît quand même à chaque mise à jour. Précacher les
 * sprites ne suffit pas non plus — mesuré, l'installation du service worker ne se
 * terminait jamais avec 15 Mo de PNG dans le glob.
 *
 * Une version dans la requête change la CLÉ de cache : le nouveau moteur ne demande plus
 * jamais l'URL qu'il a en cache pour l'ancienne. La cohérence n'est donc pas une course
 * gagnée par le réseau, elle est vraie par construction. Les entrées périmées s'effacent
 * ensuite d'elles-mêmes (`maxEntries`).
 */

import { APP_VERSION } from '@/config/changelog';

const SPRITES = '/assets/buildings';
const ATLAS = '/assets/atlas';

/**
 * URL d'un sprite, `nom` avec ou sans `.png`.
 *
 * La version est la même pour toute la session : deux sprites ne peuvent pas venir de
 * deux versions différentes, ce qui est exactement l'invariant qu'on veut.
 */
export function spriteUrl(name: string): string {
  const file = name.endsWith('.png') ? name : `${name}.png`;
  return `${SPRITES}/${file}?v=${APP_VERSION}`;
}

/** URL versionnée d'un fichier généré de l'atlas. */
export function atlasUrl(name: string): string {
  return `${ATLAS}/${name}?v=${APP_VERSION}`;
}
